import { useMemo } from "react";
import { getShortcutLabel } from "../app/keymap.ts";
import { selectionToLeafObjectIds } from "../app/objectSelection.ts";
import { saveToCloudLibrary } from "../app/saveToLibrary.ts";
import { useStore } from "../app/store.ts";
import { isBlendEntityId, storeKeyToEntityId } from "../ecs/index.ts";
import { MenuItem } from "./menu/MenuItem.tsx";
import { MenuDivider } from "./menu/MenuPrimitives.tsx";
import { Popover } from "./Popover.tsx";
import { RADIUS_HEADER } from "./tokens.ts";

export interface ContextMenuState {
  x: number;
  y: number;
}

const Divider = MenuDivider;

export function ViewportContextMenu({
  menu,
  onClose,
}: {
  menu: ContextMenuState | null;
  onClose: () => void;
}) {
  const hasSelection = useStore((s) => s.selectedObjectTargets.length > 0);
  const editMode = useStore((s) => s.editMode);
  const toggleVisibility = useStore((s) => s.toggleVisibility);
  const removeSceneObject = useStore((s) => s.removeSceneObject);
  const pushUndo = useStore((s) => s.pushUndo);

  const inObjectMode = editMode === null;

  const handleHide = () => {
    const state = useStore.getState();
    const selectedObjects = selectionToLeafObjectIds(
      state.blendFiles,
      state.collections,
      state.selectedObjectTargets,
    );
    let pushed = false;
    for (const id of selectedObjects) {
      const eid = storeKeyToEntityId(id);
      if (isBlendEntityId(eid)) {
        toggleVisibility(id);
      } else {
        if (!pushed) {
          pushUndo();
          pushed = true;
        }
        removeSceneObject(id);
      }
    }
    onClose();
  };

  const handleDuplicate = () => {
    useStore.getState().duplicate();
    onClose();
  };

  const handleAddPointLight = () => {
    useStore.getState().addLight("point");
    onClose();
  };

  const handleAddDirectionalLight = () => {
    useStore.getState().addLight("directional");
    onClose();
  };

  const anchorPoint = useMemo(
    () => (menu ? { x: menu.x, y: menu.y } : { x: 0, y: 0 }),
    [menu],
  );

  return (
    <Popover
      anchorPoint={anchorPoint}
      open={menu !== null}
      onClose={onClose}
      style={{
        borderRadius: RADIUS_HEADER,
        minWidth: 200,
        pointerEvents: "auto",
      }}
    >
      <MenuItem
        label="Hide"
        shortcut={getShortcutLabel("deleteObjects")}
        shortcutKey="h"
        disabled={!hasSelection || !inObjectMode}
        onClick={handleHide}
      />
      <MenuItem
        label="Delete"
        shortcut={getShortcutLabel("deleteObjects")}
        shortcutKey="d"
        disabled={!hasSelection || !inObjectMode}
        onClick={handleHide}
      />
      <Divider />
      <MenuItem
        label="Duplicate"
        shortcut={getShortcutLabel("duplicate")}
        shortcutKey="u"
        disabled={!hasSelection}
        onClick={handleDuplicate}
      />
      <Divider />
      <MenuItem
        label="Save to Library"
        shortcutKey="s"
        disabled={!hasSelection || !inObjectMode}
        onClick={() => {
          saveToCloudLibrary();
          onClose();
        }}
      />
      <Divider />
      <MenuItem label="Add Point Light" onClick={handleAddPointLight} />
      <MenuItem
        label="Add Directional Light"
        onClick={handleAddDirectionalLight}
      />
    </Popover>
  );
}
